import { detail, add } from '.';
import { toJS } from 'mobx';
import { openBill, refresh, goBack } from '../navigate';
import { requiredValidate, getHoverRow, BillStatusType } from '../utils';
import { PageStatusType } from '@stores/pagestore';
import { sessionStorage } from 'ynf-core-middleware'
import { isJDiwork } from '@utils';

/**
 * 编辑
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  // 选填字段
 *  targetStoreKey: string，默认值：cardStore
 *  cardStoreKey: string，默认值：cardStore，已废弃
 *  dataSourceAlias: string， 默认值：cardStore.dataSource
 *  billNo: string， 默认值：params.billNo
 *  billId: string， 默认值：params.billId
 * }
 * @returns
 * @hooks {
 *  // 专有钩子
 *  // 公共钩子
 *  beforeHook: 开始钩子
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export const edit = {
  get visible() {
    const { cardStoreKey = 'cardStore', targetStoreKey = cardStoreKey } = this.params;
    const cardStore = this.rootStore.get(cardStoreKey);
    const targetStore = this.rootStore.get(targetStoreKey);
    if (!cardStore || !targetStore) return false;
    if (targetStore === cardStore) {
      const { formStore: formStoreKey = 'formStore' } = cardStore;
      const formStore = this.rootStore.get(formStoreKey) || {};
      const isBrowse = !formStore.verifyState && cardStore.isBrowse;
      const isVerifyState = ([BillStatusType.OPEN, BillStatusType.STOP, BillStatusType.DisapproveDraft].includes(formStore.verifyState)) && cardStore.isBrowse;
      return isBrowse || isVerifyState;
    } else {
      return targetStore?.uiState === PageStatusType.BROWSE;
    }
  },
  get enable() {
    return this.visible
  },
  async doAction(rootStore: any, options: any = {}) {
    const { cardStoreKey = 'cardStore', targetStoreKey = cardStoreKey } = options;
    const targetStore = rootStore.get(targetStoreKey);
    if (!targetStore) throw (new Error(`The Store[${targetStoreKey}] not found`));
    try {
      if (targetStore.uiState !== PageStatusType.ADD) {
        targetStore.setUiState(PageStatusType.EDIT);
      }
    } catch (err: any) {
      return Promise.reject(err);
    }
  }
}

/**
 * 取消编辑
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  // 选填字段
 *  targetStoreKey: string，默认值：cardStore
 *  treeStoreKey: string， 默认值：treeStore
 *  pagingStoreKey: string，默认值：pagingStore
 *  cardStoreKey: string，默认值：cardStore，已废弃
 *  isGoBackAfterCancel: boolean， 默认值：true
 *  dataSourceAlias: string， 默认值：cardStore.dataSource
 *  billNo: string， 默认值：params.billNo
 *  billId: string， 默认值：params.billId
 * }
 * @returns
 * @hooks {
 *  // 专有钩子
 *  // 公共钩子
 *  beforeHook: 开始钩子
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export const cancelEdit = {
  get visible() {
    const { cardStoreKey = 'cardStore', targetStoreKey = cardStoreKey } = this.params;
    const cardStore = this.rootStore.get(cardStoreKey);
    const targetStore = this.rootStore.get(targetStoreKey);
    if (!cardStore || !targetStore) return false;
    if (targetStore === cardStore) {
      return cardStore?.isEdit;
    } else {
      return targetStore?.uiState === PageStatusType.EDIT || targetStore?.uiState === PageStatusType.ADD;
    }
  },
  get enable() {
    return true;
  },
  afterHook(rootStore: any, data: any) {
    const { treeStoreKey = 'treeStore' } = this.params;
    const treeStore = rootStore.get(treeStoreKey);
    if (treeStore) {
      treeStore.onSelect([treeStore?.selectedKeys?.[0]]);
    }
  },
  async doAction(rootStore: any, options: any = {}) {
    const { cardStoreKey = 'cardStore', treeStoreKey = 'treeStore', targetStoreKey = cardStoreKey, isGoBackAfterCancel } = options;
    const targetStore = rootStore.get(targetStoreKey);
    const treeStore = rootStore.get(treeStoreKey);
    if (!targetStore) throw (new Error(`The Store[${targetStoreKey}] not found`));

    if (targetStore.uiState !== PageStatusType.ADD) {
      if (targetStore.storeType === 'CardStore') {
        await rootStore.utils.confirm({ title: lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B8003F', '确认取消编辑?'), content: lang.templateByUuid('UID:P_CORE-YNF-FE_19201F8E04500009', '您将取消编辑并转为浏览状态'), ...options.confirmOptions });
        targetStore.setUiState(PageStatusType.BROWSE);
      } else if (targetStore.storeType === 'TableStore') {
        const changedRows = targetStore.getChangedRows();
        const deleteRowsCount = changedRows.filter((v: any) => v.getValue('_status') === 'Delete').length;
        const insertRowsCount = changedRows.filter((v: any) => v.getValue('_status') === 'Insert').length;

        // 状态还原
        targetStore.setUiState(PageStatusType.BROWSE);
        targetStore.cancelEdit();

        // 行编辑表, 关联分页
        const { pagingStoreKey = 'pagingStore' } = options;
        const pagingStore = rootStore.get(pagingStoreKey);
        if (options.pagingStoreKey) {
          if (!pagingStore) throw (new Error(`The Store[${pagingStoreKey}] not found`));
        }
        if (pagingStore) pagingStore.setPageInfo({ total: pagingStore.total + deleteRowsCount - insertRowsCount });
      } else {
        targetStore.setUiState(PageStatusType.BROWSE);
        targetStore.cancelEdit();
      }
    } else {
      if (targetStore.storeType === 'CardStore') {
        await rootStore.utils.confirm({ title: lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B8003F', '确认取消编辑?'), content: lang.templateByUuid('UID:P_CORE-YNF-FE_19201FC605900003', '您将取消编辑并回退'), ...options.confirmOptions });
        if (!treeStore) {
          if (isGoBackAfterCancel !== false) {
            goBack.doAction.call(this, rootStore);
            return
          }
        }
        await add.call(this, rootStore, options);
        targetStore.setUiState(PageStatusType.BROWSE);
      } else if (targetStore.storeType === 'TableStore') {
        const changedRows = targetStore.getChangedRows();
        const deleteRowsCount = changedRows.filter((v: any) => v.getValue('_status') === 'Delete').length;
        const insertRowsCount = changedRows.filter((v: any) => v.getValue('_status') === 'Insert').length;

        // 状态还原
        targetStore.cancelEdit();

        // 行编辑表, 关联分页
        const { pagingStoreKey = 'pagingStore' } = options;
        const pagingStore = rootStore.get(pagingStoreKey);
        if (options.pagingStoreKey) {
          if (!pagingStore) throw (new Error(`The Store[${pagingStoreKey}] not found`));
        }
        if (pagingStore) pagingStore.setPageInfo({ total: pagingStore.total + deleteRowsCount - insertRowsCount });
      } else {
        targetStore.cancelEdit();
      }
    }
  }
}

/**
 * 行编辑
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  billNo: string，跳转卡片页的billNo
 *  tableStoreKey: string，默认值：tableStore
 *  filterStoreKey: string，默认值：filterStore
 *  pagingStoreKey: string，默认值：pagingStore
 *  // 选填字段
 *  mode: string, 默认值：edit
 * }
 * @hooks {
 *  // 专有钩子
 *  // 公共钩子
 *  beforeHook: 开始钩子
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export const editByRow = {
  get isOperationLog() {
    const { type } = this.params;
    if (type === 'modal') {
      return true;
    } else if (type === 'tab' && isJDiwork()) {
      return false;
    } else {
      const { pageStore = {} } = this.rootStore;
      const { generalSetting = {} } = pageStore;
      return generalSetting.tabOpenMode !== 1;
    }
  },
  async doAction (rootStore: any, options: any = {}) {
    if (!options.mode) {
      options.mode = 'edit';
    }
    return browseByRow.doAction.apply(this, arguments);
  }
}

/**
 * 行浏览
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  billNo: string，跳转卡片页的billNo
 *  tableStoreKey: string，默认值：tableStore
 *  filterStoreKey: string，默认值：filterStore
 *  pagingStoreKey: string，默认值：pagingStore
 *  // 选填字段
 *  mode: string, 默认值：browse
 * }
 * @hooks {
 *  // 专有钩子
 *  // 公共钩子 
 *  beforeHook: 开始钩子 
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export const browseByRow = {
  get isOperationLog() {
    const { type } = this.params;
    if (type === 'modal') {
      return true;
    } else if (type === 'tab' && isJDiwork()) {
      return false;
    } else {
      const { pageStore = {} } = this.rootStore;
      const { generalSetting = {} } = pageStore;
      return generalSetting.tabOpenMode !== 1;
    }
  },
  async doAction (rootStore: any, options: any = {}) {
    requiredValidate.call(this, options, ['billNo']);
    const { tableStoreKey = 'tableStore', filterStoreKey = 'filterStore', pagingStoreKey = 'pagingStore', ...params } = options;
    const [hoverRow, tableStore] = getHoverRow.apply(this, arguments);
    if (!hoverRow) throw (new Error(`The hoverRow not found`));
    
    const currentRowInfo = {
      // 当前的单据ID
      id: hoverRow.getValue('id') ?? '',
      // 当前页面的单据ID 集合
      tableDataIds: (toJS(rootStore?.[tableStoreKey]?.getValue?.()) || []).map((item: any) => {
        return item?.rowKey ?? ''
      }),
      // 当前页面的查询条件
      condition: rootStore?.[filterStoreKey]?.getAllCondition?.() ?? {},
      // 当前页面的数据的分页信息
      paging: rootStore?.[pagingStoreKey]?.getPageInfo?.() || {}
    }
    // --TODO: 详情页面，上翻、下翻需要的数据，无法通过参数传递，需要存储起来使用，暂时使用 sessionStorge 存储；后期 store 层有了合理的规范后，建议放到一个合适的 store 里；
    sessionStorage.setItem(`moveActionParams_${options.billNo}`, JSON.stringify(currentRowInfo))
    return openBill.doAction.call(this, rootStore, {
      mode: 'browse',
      ...params,
      billNo: options.billNo,
      billId: hoverRow.getValue('id'),
      code: hoverRow.getValue('code'),
    });
  }
}
